import { createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";
import { AgentSessionEvent } from "./agentSessionEventSlice";
import { createRestSlice } from "./restSlice";

export type AgentSessionUsage = {
  sessionStart: number;
  sessionEnd: number;
  sessionDuration: number;
  ttsDuration: number;
  ttsCachedDuration: number;
  moduleDuration: number;
};

export type Test = {
  name: string;
  options: Record<string, number>;
  value: string;
  description?: string;
};

export type OutcomeType = "boolean" | "integer" | "float" | "string";

export type Outcome = {
  name: string;
  type: OutcomeType;
  value: string;
  description?: string;
};

export type Testing = {
  tests: Record<string, Test>;
  outcomes: Record<string, Outcome>;
};

export type AgentSession = {
  id: string;
  agentId: string;
  agentEnvironmentId: string;
  environmentId?: string | null;
  releaseId?: string | null;
  deploymentId?: string; // References the deployment that was active when this session was created
  status: string;
  direction: "inbound" | "outbound";
  origin: "debugger" | "web" | "phone" | "simulation" | "unknown";
  events: AgentSessionEvent[];
  usage: AgentSessionUsage;
  metrics?: Record<string, unknown>;
  telephonyCallId: string | null;
  telephonyId: string | null;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  testing?: Testing;
  /** Net cost in microdollars across credit_transaction rows referencing this
   *  session. Surfaced by the agent sessions list endpoint as a per-row rollup
   *  so the table can render a Cost column without an N+1. Older sessions
   *  predating the rollup may carry 0; that's a real-zero, not a fetch error. */
  totalCostMicrodollars?: number;
  /** GCP region the session's engine ran in (e.g. "us-central1"), mapped
   *  server-side from internal cluster naming. Absent for older sessions and
   *  local dev — render nothing in that case. */
  servingRegion?: string;
};

const agentSessionSlice = createRestSlice<AgentSession>("agentSessions", "/v1/agents/:agentId/sessions");

// Override getById to use the flat /v1/sessions/:id endpoint (the agent-scoped
// path requires admin permissions after the controller rewrite).
const getById = createAsyncThunk("agentSessions/getById", async (item: Partial<AgentSession>) => {
  const { data } = await makeRequest<{ data: AgentSession }>(`/v1/sessions/${item.id}`);
  return data;
});

export const agentSessionReducer = agentSessionSlice.reducer;
export const agentSessionActions = { ...agentSessionSlice.actions, getById };
